import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getInvoiceDetailsById,
  selectInvoice,
  downloadInvoiceDetailsById,
} from '../redux/feature/invoice/slice';
import { Card, Table, Spin } from 'antd';
import { formatedPrice } from '../utils/helper';
import { Icon } from '@iconify/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AddCard from '../components/addCard';
import { debounce } from 'lodash';

const Invoices = () => {
  const dispatch = useDispatch();
  const { invoiceId } = useParams();
  const invoice = useSelector(selectInvoice);
  const [loader, setLoader] = useState(true);
  const [stripepromise, setStripePromise] = useState();
  const [isDownloading, setIsDownloading] = useState(false);

  const connectedAccId =
    invoice?.invoiceAccociateCompanyId?.stripeAccountType === 'oauth'
      ? invoice?.invoiceAccociateCompanyId?.stripeUserId
      : invoice?.invoiceAccociateCompanyId?.stripeId;

  useEffect(() => {
    if (connectedAccId) {
      const stripeKey = process.env.REACT_APP_PUBLIC_STRIPE_KEY;
      const promise = loadStripe(stripeKey, {
        stripeAccount: connectedAccId,
      });
      setStripePromise(promise);
    }
  }, [connectedAccId]);

  useEffect(() => {
    if (invoiceId) {
      dispatch(getInvoiceDetailsById(invoiceId));
    }
    setLoader(false);
  }, [dispatch, invoiceId]);

  const getAllPayments = (reservations) => {
    let payments = [];
    reservations?.forEach((reservation) =>
      reservation.trips?.forEach((trip) =>
        payments.push(...(trip.payments || []))
      )
    );
    return payments.sort(
      (a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)
    );
  };

  const handleDownloadClick = debounce(async () => {
    setIsDownloading(true);
    try {
      await dispatch(downloadInvoiceDetailsById(invoiceId));
    } catch (error) {
      console.error('Error during download dispatch:', error);
    } finally {
      setIsDownloading(false);
    }
  }, 500);



  /* ─── Payment Summary ──────────────────────────── */
  const totalAmount = invoice?.total || 0;
  const totalDue = invoice?.due || 0;
  const totalPaid = totalAmount - totalDue;
  const paidPercent =
    totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0;

  /* ─── Table columns ────────────────────────────── */
  const columns = [
    {
      title: 'Res Details',
      dataIndex: 'resID',
      key: 'resID',
      render: (text, record) => (
        <span className="flex flex-col">
          <span className="flex items-center">
            {text}
            <div className="rounded-full px-1 py-0.5 w-5 text-center text-xs font-normal text-white bg-[#058CBD] ml-1">
              {record.trips?.length || 0}
            </div>
          </span>
          <span className="text-sm text-gray-500">{record?.pickupDate}</span>
        </span>
      ),
    },
    {
      title: 'Contact',
      dataIndex: ['customer', 'firstName'],
      key: 'contact',
      render: () => (
        <span className="flex flex-col">
          <span className="text-sm">{`${invoice?.customer?.firstName || ''} ${invoice?.customer?.lastName || ''}`}</span>
          <span className="text-xs sm:text-[12px] text-gray-500">
            {invoice?.customer?.email}
          </span>
        </span>
      ),
    },
    {
      title: 'Phone',
      dataIndex: ['customer', 'phoneNumber'],
      key: 'phone',
      render: () => (
        <span className="flex flex-col">
          <span className="text-sm">{invoice?.customer?.phoneNumber}</span>
        </span>
      ),
    },
    {
      title: 'Order Type',
      dataIndex: 'orderType',
      key: 'orderType',
      render: (text) => text,
    },
    {
      title: 'Amount',
      dataIndex: 'total',
      key: 'amount',
      render: (text) => formatedPrice(text),
    },
    {
      title: 'Payment',
      dataIndex: 'payStatus',
      key: 'payment',
      render: (_, record) => {
        let status, bgColor, textColor, icon;
        if (record.due === 0) {
          status = 'Paid';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'mdi:lock';
        } else if (record.total > record.due) {
          status = 'Partial';
          bgColor = '#FEF9C3';
          textColor = '#D49F27';
          icon = 'mdi:alert-outline';
        } else {
          status = 'Unpaid';
          bgColor = '#FEE2E2';
          textColor = '#DC2626';
          icon = 'mdi:alert-circle-outline';
        }
        return (
          <div
            className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
            <span className="text-xs font-semibold" style={{ color: textColor }}>
              {status}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Res Status',
      dataIndex: 'resStatus',
      key: 'resStatus',
      render: (status) => {
        let displayText, bgColor, textColor, icon;
        if (status === 'confirmed') {
          displayText = 'Open';
          bgColor = '#C8EBF7';
          textColor = '#058CBD';
          icon = 'mdi:folder-open-outline';
        } else if (status === 'completed') {
          displayText = 'Closed';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'mdi:lock';
        }
        return (
          <div
            className="inline-flex items-center rounded-lg cursor-pointer hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
            <span className="text-xs font-semibold" style={{ color: textColor }}>
              {displayText}
            </span>
          </div>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    const tripColumns = [
      {
        title: 'Trip Details',
        dataIndex: 'tripId',
        key: 'tripId',
        render: (text, tripRecord) => (
          <span className="flex flex-col">
            <span>{text}</span>
            <span className="text-sm text-gray-500">{tripRecord?.pickupDate}</span>
          </span>
        ),
      },
      {
        title: 'Contact',
        dataIndex: ['customer', 'firstName'],
        key: 'contact',
        render: () => (
          <span className="flex flex-col">
            <span className="text-sm">{`${invoice?.customer?.firstName || ''} ${invoice?.customer?.lastName || ''}`}</span>
            <span className="text-xs sm:text-[12px] text-gray-500">
              {invoice?.customer?.email}
            </span>
          </span>
        ),
      },
      {
        title: 'Phone',
        dataIndex: ['customer', 'phoneNumber'],
        key: 'phone',
        render: () => (
          <span className="flex flex-col">
            <span className="text-sm">{invoice?.customer?.phoneNumber}</span>
          </span>
        ),
      },
      {
        title: 'Order Type',
        dataIndex: 'orderType',
        key: 'orderType',
        render: (text) => text,
      },
      {
        title: 'Amount',
        dataIndex: 'total',
        key: 'amount',
        render: (text) => formatedPrice(text),
      },
      {
        title: 'Payment',
        dataIndex: 'payStatus',
        key: 'payment',
        render: (_, tripRecord) => {
          let status, bgColor, textColor, icon;
          if (tripRecord.paid === 0) {
            status = 'Unpaid';
            bgColor = '#FEE2E2';
            textColor = '#DC2626';
            icon = 'mdi:alert-circle-outline';
          } else if (tripRecord.paid < tripRecord.total) {
            status = 'Partial';
            bgColor = '#FEF9C3';
            textColor = '#D49F27';
            icon = 'mdi:alert-outline';
          } else {
            status = 'Paid';
            bgColor = '#DCFCE7';
            textColor = '#16A34A';
            icon = 'mdi:cash-check';
          }
          return (
            <div
              className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
              style={{ backgroundColor: bgColor }}
            >
              <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
              <span className="text-xs font-semibold" style={{ color: textColor }}>
                {status}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Trip Status',
        dataIndex: 'tripStatus',
        key: 'tripStatus',
        render: (status) => {
          let displayText, bgColor, textColor, icon;
          if (status === 'confirmed') {
            displayText = 'Confirmed';
            bgColor = '#C8EBF7';
            textColor = '#058CBD';
            icon = 'mdi:check-decagram';
          } else if (status === 'completed') {
            displayText = 'Completed';
            bgColor = '#DCFCE7';
            textColor = '#16A34A';
            icon = 'mdi:check-circle-outline';
          }
          return (
            <div
              className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
              style={{ backgroundColor: bgColor }}
            >
              <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
              <span className="text-xs font-semibold" style={{ color: textColor }}>
                {displayText}
              </span>
            </div>
          );
        },
      },
    ];

    return (
      <Table
        columns={tripColumns}
        dataSource={record?.trips}
        pagination={false}
        rowKey="tripId"
        className="nested-table"
        bordered={false}
        components={{
          header: {
            cell: ({ children, ...restProps }) => (
              <th {...restProps}>{children}</th>
            ),
          },
        }}
      />
    );
  };

  const paymentColumns = [
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 150,
      render: (text) => <span>{new Date(text).toLocaleDateString()}</span>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (text) => formatedPrice(text),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 150,
      render: (text) => {
        let displayText, bgColor, textColor, icon;
        if (text === 'cash') {
          displayText = 'Cash';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'mdi:cash-check';
        } else if (text === 'card') {
          displayText = 'Card';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'majesticons:creditcard';
        }
        return (
          <div
            className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
            <span className="text-xs font-semibold" style={{ color: textColor }}>
              {displayText}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (text) => {
        let displayText, bgColor, textColor, icon;
        if (text === 'refunded') {
          displayText = 'Refunded';
          bgColor = '#FEE2E2';
          textColor = '#DC2626';
          icon = 'mdi:alert-circle-outline';
        } else if (text === 'completed') {
          displayText = 'Completed';
          bgColor = '#DCFCE7';
          textColor = '#16A34A';
          icon = 'mdi:check-circle-outline';
        }
        return (
          <div
            className="inline-flex items-center cursor-pointer rounded-lg hover:scale-105 font-normal whitespace-nowrap capitalize transition-transform duration-300 ease-in-out px-3 py-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon icon={icon} className="w-4 h-4 mr-1" style={{ color: textColor }} />
            <span className="text-xs font-semibold" style={{ color: textColor }}>
              {displayText}
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="checkout-page bg-gray-100 min-h-screen p-6" style={{ zoom: '0.8' }}>
      {loader || !invoice?._id ? (
        <div className="flex justify-center items-center h-screen">
          <Spin size="large" />
        </div>
      ) : (
        <div className="checkout-content md:max-w-5xl mx-auto space-y-6">
          {/* ─── Header ──────────────────────────── */}
          <div className="flex flex-col md:flex-row justify-between items-start">
            <div className="flex flex-col items-start space-y-8">
              <div className="w-24 h-12 rounded-md overflow-hidden">
                <img
                  src={invoice?.invoiceAccociateCompanyId?.logo}
                  alt="company logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-semibold">
                {invoice?.invoiceAccociateCompanyId?.name}
              </span>
            </div>

            <div className="flex flex-col items-end space-y-2 mt-4 md:mt-0">
              <span className="text-xl font-semibold">
                Total - {formatedPrice(invoice?.total)}
              </span>
              <span className="text-xl font-semibold">
                Total Due - {formatedPrice(invoice?.due)}
              </span>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <button
                  className="border-2 border-[#058CBD] rounded-md text-sm font-semibold px-4 py-2 text-[#058CBD] flex items-center space-x-2 hover:bg-[#058CBD] hover:text-white transition-colors duration-300"
                  onClick={() => handleDownloadClick()}
                >
                  {isDownloading ? (
                    <Icon icon="line-md:loading-loop" className="text-xl" />
                  ) : (
                    <Icon icon="line-md:download-loop" className="text-xl" />
                  )}
                  <span>Download Summary</span>
                </button>


              </div>
            </div>
          </div>

          {/* ─── Payment Summary Card ────────────── */}
          <Card className="shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex-1 w-full">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Payment Progress</span>
                  <span className="font-semibold text-[#058CBD]">{paidPercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${paidPercent}%`,
                      background: paidPercent === 100
                        ? '#16A34A'
                        : 'linear-gradient(90deg, #058CBD, #01A7DC)',
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs mt-2 text-gray-500">
                  <span>Paid: {formatedPrice(totalPaid)}</span>
                  <span>Remaining: {formatedPrice(totalDue)}</span>
                </div>
              </div>
              {totalDue > 0 && (
                <a
                  href="#make-payment"
                  className="bg-[#058CBD] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#01A7DC] transition-colors text-sm whitespace-nowrap"
                >
                  Pay Now
                </a>
              )}
            </div>
          </Card>

          {/* ─── Reservations Table ──────────────── */}
          <Card
            className="shadow-lg"
            title={`Invoice No: ${invoice?.invoiceNumber || 'Loading...'}`}
          >
            <div className="overflow-auto">
              <Table
                columns={columns}
                expandable={{
                  expandedRowRender,
                  rowExpandable: (record) =>
                    record?.trips && record?.trips.length > 0,
                }}
                dataSource={invoice?.reservation}
                rowKey="resID"
                pagination={{
                  pageSize: 10,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                  className: 'pagination-control',
                }}
              />
            </div>
          </Card>

          {/* ─── Previous Payments ───────────────── */}
          <Card className="shadow-lg" title="Previous Payments">
            <div className="overflow-x-auto">
              <Table
                columns={paymentColumns}
                dataSource={getAllPayments(invoice?.reservation)}
                rowKey="_id"
                className="min-w-full"
                pagination={{
                  pageSize: 5,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                  className: 'pagination-control',
                }}
              />
            </div>
          </Card>

          {/* ─── Make a Payment (charge) ─────────── */}
          <div id="make-payment">
            <Card className="shadow-lg" title="Make a Payment">
              {stripepromise && (
                <Elements stripe={stripepromise}>
                  <AddCard
                    invoiceId={invoiceId}
                    ConnAccId={connectedAccId}
                    remainingAmount={invoice?.due}
                  />
                </Elements>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
